/**
 * Currency Formatting Utilities
 * 
 * Provides utilities for properly formatting and rounding monetary values
 * to prevent floating point precision issues (e.g., 1.000003, 8.9999999)
 */

/**
 * Rounds a monetary value to a specified number of decimal places
 * Uses Number.EPSILON to handle floating point precision issues
 * 
 * @param value The monetary value to round
 * @param decimals Number of decimal places (default: 2)
 * @returns Rounded value
 * 
 * @example
 * roundMonetaryValue(1.0000003, 2) // Returns 1.00
 * roundMonetaryValue(8.9999999, 2) // Returns 9.00
 * roundMonetaryValue(10.555, 2) // Returns 10.56
 */
export function roundMonetaryValue(value: number, decimals: number = 2): number {
  if (typeof value !== 'number' || isNaN(value) || !isFinite(value)) {
    return 0;
  }
  
  const factor = Math.pow(10, decimals);
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * Formats a monetary value with proper rounding before applying toFixed
 * This prevents displaying values like 1.000003 or 8.9999999
 * 
 * @param value The monetary value to format
 * @param decimals Number of decimal places (default: 2)
 * @returns Formatted string with proper decimal places
 * 
 * @example
 * formatMonetaryValue(1.0000003, 2) // Returns "1.00"
 * formatMonetaryValue(8.9999999, 2) // Returns "9.00"
 */
export function formatMonetaryValue(value: number, decimals: number = 2): string {
  const rounded = roundMonetaryValue(value, decimals);
  return rounded.toFixed(decimals);
}

/**
 * Sanitizes and validates an amount string from query parameters or user input
 * Handles edge cases like invalid strings, negative values, excessive decimals, etc.
 * 
 * @param amountStr The amount string to sanitize (e.g., from URL query params)
 * @param maxDecimals Maximum number of decimal places allowed (default: 8 for high precision currencies)
 * @param allowNegative Whether to allow negative values (default: false)
 * @returns Sanitized number or null if invalid
 * 
 * @example
 * sanitizeAmount("0.30000") // Returns 0.3
 * sanitizeAmount("1.234567890") // Returns 1.23456789 (rounded to maxDecimals)
 * sanitizeAmount("invalid") // Returns null
 * sanitizeAmount("-5.00") // Returns null (negative not allowed by default)
 * sanitizeAmount("", 2) // Returns null
 */
export function sanitizeAmount(
  amountStr: string | null | undefined,
  maxDecimals: number = 8,
  allowNegative: boolean = false
): number | null {
  // Handle null, undefined, or empty strings
  if (!amountStr || typeof amountStr !== 'string') {
    return null;
  }

  // Trim whitespace
  const trimmed = amountStr.trim();
  if (trimmed === '') {
    return null;
  }

  // Remove any non-numeric characters except decimal point and minus sign
  // This handles cases like "$100", "100.00 USD", etc.
  const cleaned = trimmed.replace(/[^\d.-]/g, '');
  
  // Check if result is empty after cleaning
  if (cleaned === '' || cleaned === '.' || cleaned === '-') {
    return null;
  }

  // Parse to number
  const parsed = parseFloat(cleaned);
  
  // Validate parsed value
  if (isNaN(parsed) || !isFinite(parsed)) {
    return null;
  }

  // Check for negative values if not allowed
  if (!allowNegative && parsed < 0) {
    return null;
  }

  // Round to maxDecimals to clean up excessive precision
  const sanitized = roundMonetaryValue(parsed, maxDecimals);
  
  // Final validation - ensure it's still a valid number after rounding
  if (isNaN(sanitized) || !isFinite(sanitized)) {
    return null;
  }

  return sanitized;
}

/** Stripe zero-decimal currencies (amount is already the smallest unit). */
const STRIPE_ZERO_DECIMAL = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

/**
 * Convert a major-unit amount to Stripe Payment Request / PaymentIntent minor units.
 */
export function toStripeMinorUnits(amount: number, currency: string): number {
  const code = currency.toUpperCase();
  if (STRIPE_ZERO_DECIMAL.has(code)) {
    return Math.round(amount);
  }
  return Math.round(amount * 100);
}

